import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import CategoryIcon from '@mui/icons-material/Category';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material';

import { serviceTypeEnum } from '../../serviceTypes/types';

//TravelFileServiceStatus

export type TravelFileServiceStatusTypes =
    | 'confirmed'
    | 'request'
    | 'waiting list'
    | 'sold out'
    | 'request for cancellation'
    | 'cancellation confirmed'
    | 'unconfirmed';
export const travelFileServiceStatusTypesEnum: {
    [
        key in
            | 'CONFIRMED'
            | 'REQUEST'
            | 'WAITING_LIST'
            | 'SOLD_OUT'
            | 'REQUEST_FOR_CANCELLATION'
            | 'CANCELLATION_CONFIRMED'
            | 'UNCONFIRMED'
    ]: TravelFileServiceStatusTypes;
} = {
    CONFIRMED: 'confirmed',
    REQUEST: 'request',
    WAITING_LIST: 'waiting list',
    SOLD_OUT: 'sold out',
    REQUEST_FOR_CANCELLATION: 'request for cancellation',
    CANCELLATION_CONFIRMED: 'cancellation confirmed',
    UNCONFIRMED: 'unconfirmed'
};
export const travelFileServiceStatusTypesOptions = Object.values(
    travelFileServiceStatusTypesEnum
) as TravelFileServiceStatusTypes[];

// TravelFileServiceTypes must be the model name

export type TravelFileServiceTypes =
    'tours' | 'flights' | 'guides' | 'hotels' | 'generalServices' | 'packages' | 'visaServices';
export type TravelFileServiceTypesKeys =
    'TOURS' | 'FLIGHTS' | 'GUIDES' | 'HOTELS' | 'GENERALS' | 'PACKAGES' | 'VISAS';
export const travelFileServiceTypesEnum: {
    [key in TravelFileServiceTypesKeys]: TravelFileServiceTypes;
} = {
    TOURS: 'tours',
    FLIGHTS: 'flights',
    GUIDES: 'guides',
    HOTELS: 'hotels',
    GENERALS: 'generalServices',
    PACKAGES: 'packages',
    VISAS: 'visaServices'
} as const satisfies { [key in TravelFileServiceTypesKeys]: TravelFileServiceTypes };
/**
 * Service type → the permission key for each action on it.
 *
 * Spelled out in full rather than composed as `${resource}:${action}`, so every
 * key is greppable and the catalog-coverage test can see it. A key built by
 * string interpolation is invisible to any audit of which permissions the code
 * actually reads — which is how these came to be checked only through the
 * wildcard `service.*:cancel`, granting cancellation of EVERY service type to
 * anyone allowed to cancel ONE.
 *
 * Only actions that are genuinely enforced appear here; adding a key you do not
 * check would defeat the coverage test rather than satisfy it.
 */
export const SERVICE_ACTION_PERMISSIONS: Record<
    TravelFileServiceTypes,
    { create: string; delete: string; cancel: string }
> = {
    tours: {
        create: 'service.tour:create',
        delete: 'service.tour:delete',
        cancel: 'service.tour:cancel'
    },
    flights: {
        create: 'service.flight:create',
        delete: 'service.flight:delete',
        cancel: 'service.flight:cancel'
    },
    guides: {
        create: 'service.guide:create',
        delete: 'service.guide:delete',
        cancel: 'service.guide:cancel'
    },
    hotels: {
        create: 'service.hotel:create',
        delete: 'service.hotel:delete',
        cancel: 'service.hotel:cancel'
    },
    generalServices: {
        create: 'service.general:create',
        delete: 'service.general:delete',
        cancel: 'service.general:cancel'
    },
    packages: {
        create: 'service.package:create',
        delete: 'service.package:delete',
        cancel: 'service.package:cancel'
    },
    visaServices: {
        create: 'service.visa:create',
        delete: 'service.visa:delete',
        cancel: 'service.visa:cancel'
    }
};

/** Preview name ("Flight", "Tour" …) → the service type key above. */
export const SERVICE_TYPE_BY_PREVIEW_NAME: Record<string, TravelFileServiceTypes> = {
    Tour: 'tours',
    Flight: 'flights',
    Guide: 'guides',
    Hotel: 'hotels',
    General: 'generalServices',
    Package: 'packages',
    Visa: 'visaServices'
};

export type TravelFileServicePreviewNames =
    'Tour' | 'Flight' | 'Guide' | 'Hotel' | 'General' | 'Package' | 'Visa';
export type TravelFileServicePreviewNamesKeys =
    'TOUR' | 'FLIGHT' | 'GUIDE' | 'HOTEL' | 'GENERAL' | 'PACKAGE' | 'VISA';
export const TravelFileServicePreviewNamesEnum: {
    [key in TravelFileServicePreviewNamesKeys]: TravelFileServicePreviewNames;
} = {
    TOUR: 'Tour',
    FLIGHT: 'Flight',
    GUIDE: 'Guide',
    HOTEL: 'Hotel',
    GENERAL: 'General',
    PACKAGE: 'Package',
    VISA: 'Visa'
};

export type ServicesInfoMapValueType = {
    label: TravelFileServicePreviewNames;
    icon: OverridableComponent<SvgIconTypeMap>;
};

export type ServicesInfoMapType = Record<TravelFileServiceTypes, ServicesInfoMapValueType>;

export const SERVICES_INFO_MAP: ServicesInfoMapType = {
    tours: { label: TravelFileServicePreviewNamesEnum.TOUR, icon: DirectionsBusIcon },
    flights: { label: TravelFileServicePreviewNamesEnum.FLIGHT, icon: FlightIcon },
    guides: { label: TravelFileServicePreviewNamesEnum.GUIDE, icon: RecordVoiceOverIcon },
    hotels: { label: TravelFileServicePreviewNamesEnum.HOTEL, icon: HotelIcon },
    generalServices: { label: TravelFileServicePreviewNamesEnum.GENERAL, icon: CategoryIcon },
    packages: { label: TravelFileServicePreviewNamesEnum.PACKAGE, icon: WorkIcon },
    visaServices: { label: TravelFileServicePreviewNamesEnum.VISA, icon: AccountBalanceIcon }
};

export const travelFileServicePreviewNameToModelType: {
    [key in TravelFileServicePreviewNames]: TravelFileServiceTypes;
} = {
    Tour: travelFileServiceTypesEnum.TOURS,
    Flight: travelFileServiceTypesEnum.FLIGHTS,
    Guide: travelFileServiceTypesEnum.GUIDES,
    Hotel: travelFileServiceTypesEnum.HOTELS,
    General: travelFileServiceTypesEnum.GENERALS,
    Package: travelFileServiceTypesEnum.PACKAGES,
    Visa: travelFileServiceTypesEnum.VISAS
};

export const travelFileServiceModelTypeToPreviewName: {
    [key in TravelFileServiceTypes]: TravelFileServicePreviewNames;
} = {
    tours: TravelFileServicePreviewNamesEnum.TOUR,
    flights: TravelFileServicePreviewNamesEnum.FLIGHT,
    guides: TravelFileServicePreviewNamesEnum.GUIDE,
    hotels: TravelFileServicePreviewNamesEnum.HOTEL,
    generalServices: TravelFileServicePreviewNamesEnum.GENERAL,
    packages: TravelFileServicePreviewNamesEnum.PACKAGE,
    visaServices: TravelFileServicePreviewNamesEnum.VISA
};
export const travelFileServiceTypesOptions: Array<TravelFileServiceTypes> =
    Object.values(travelFileServiceTypesEnum);

export const travelFileServicePreviewNamesOptions: Array<TravelFileServicePreviewNames> = Object.values(
    TravelFileServicePreviewNamesEnum
);

const SERVICE_TYPE_NAME_MAP = [
    [serviceTypeEnum.FLIGHT, travelFileServiceTypesEnum.FLIGHTS],
    [serviceTypeEnum.ESCORTED_TOUR, travelFileServiceTypesEnum.TOURS],
    [serviceTypeEnum.GUIDE, travelFileServiceTypesEnum.GUIDES],
    [serviceTypeEnum.HOTEL, travelFileServiceTypesEnum.HOTELS],
    [serviceTypeEnum.GENERAL, travelFileServiceTypesEnum.GENERALS],
    [serviceTypeEnum.PACKAGE, travelFileServiceTypesEnum.PACKAGES],
    [serviceTypeEnum.VISA, travelFileServiceTypesEnum.VISAS]
] as const;
export const serviceTypeNameMap = SERVICE_TYPE_NAME_MAP.reduce((acc, arr) => {
    acc[arr[0]] = arr[1];
    acc[arr[1]] = arr[0];
    return acc;
}, {} as any);
